<script>
import {mapActions, mapState} from 'vuex';
import { callAction, saveApplication } from '@/app/api';
import {isArray, isObject} from '@incodeapps/js-utilities';
import ApplicationConfig from '../config';
import Program from "../Program.vue";
import {throttle} from "throttle-debounce";
import ApplicationValidator from "ApplicationValidator";
import checkMark from "@/assets/img/icons/check-mark.svg";
import exclamationMark from "@/assets/img/icons/exclamation-mark.svg";
import garbageCan from "@/assets/img/icons/garbage-can.svg";

export default {
	filters: {
		formatMoney(value) {
			return new Intl.NumberFormat('en-US', {minimumFractionDigits: 2,}).format(value);
		},
	},
	components: {
		Program
	},
	data() {
		return {
			//icons
			checkMark: checkMark,
			exclamationMark: exclamationMark,
			garbageCan: garbageCan,
			//values
			applicant: {},
			coApps: [],
			errors: {},
			equipments: [],
			options: {},
			selectedAppIndex: 'main',
			app: null,
			alreadySaved: false,
			programType: '',
			isMaxApplicants: false,
			addApplicantVModels: {
				firstName: '',
				middleInitial: '',
				lastName: '',
				email: '',
				address1: '',
				address2: '',
				city: '',
				province: '',
				postal: '',
				titleOwner: null
			},
			canadaPostScriptsLoaded: false,
			canadaPostControl: null,
			addApplicantErrors: {},
			vModels: {
				firstName: '',
				middleInitial: '',
				lastName: '',
				email: '',
				sin: '',
				dob: '',
				primaryPhone: '',
				mobilePhone: '',
				country: 'Canada',
				address1: '',
				address2: '',
				province: '',
				city: '',
				postal: '',
				titleOwner: null,
				grossIncome: '',
				linkSentOn: null,
				linkPurpose: null,
				token: null,
				program: {},
				employer: '',
				employmentTitle: '',
				employmentTerm: ''
			},
			optionals: [
				'mobilePhone',
				'sin',
				'referral',
			]
		};
	},
	props: {
		application: {
			type: Object,
			default: null
		},
		getApplication: {
			type: Function,
			default: () => {}
		},
		exitApplication: {
			type: Function,
			default: () => {}
		},
		exiting: {
			type: Boolean,
			default: false
		},
		goToNextStep: {
			type: Function,
			default: () => {}
		},
		goToPreviousStep: {
			type: Function,
			default: () => {}
		},
		nextStep: {
			type: Boolean,
			default: false
		},
		previousStep: {
			type: Boolean,
			default: false
		},
		saveStep: {
			type: Object,
			default: {
				saving: false
			}
		},
		setNextDisabled: {
			type: Function,
			default: () => {}
		},
		type: {
			type: String,
			default: ''
		}
	},
	watch: {
		'applicant.province': function(newProvince, oldProvince) {
            if (newProvince && oldProvince && newProvince !== oldProvince) {
                this.updateLeaseTaxesInBackground(newProvince);
            }
        },
		optionSets: {
			handler() {
				if (this.vModels) {
					Object.keys(this.vModels).forEach(key => {
						this.checkForOptions(key);
					});
				}
			}
		},
		"exiting": "saveAndMove",
		"nextStep": "saveAndMove",
		"previousStep": "saveAndMove",
		"saveStep.saving": "saveAndMove",
	},
	async mounted() {
		this.saveLoading(true);
		this.app = this.application;
		this.applicant = this.makeApplicantObject(this.getApplicant());
		if (this.app.application.coApps) {
			this.coApps = this.normalizeCoAppTitleOwners(this.app.application.coApps);
			this.updateIsMaxApplicants();
		} else {
			this.coApps = []
			this.isMaxApplicants = false;
		}
		if (this.app.application.equipments) {
			this.equipments = this.app.application.equipments
		}
		const shouldOpenNewApplicantForm = this.$route.query.newApplicant === '1' && this.hasApplicant && !this.isMaxApplicants;
		const autoSelectedIndex = this.$route.query?.select;
		if (autoSelectedIndex && this.hasApplicant) {
			const index = (autoSelectedIndex ?? "main") === "main" ? "main" : Number(autoSelectedIndex);
			await this.selectApp(index, false);
		} else {
			await this.selectApp('main', false);
		}
		// Only show Save when adding a brand-new applicant
		this.setNextDisabled(!this.hasApplicant);
		if (shouldOpenNewApplicantForm) {
			await this.prepareNewApplicant();
		}
		this.vModels.program = this.app.application?.program;
		this.programType = this.type.charAt(0).toUpperCase() + this.type.slice(1);
		this.saveLoading(false);
	},
	computed: {
		addressForm() {
			return this.getForm('2.1');
		},
		hasApplicant() {
			const currentApplicantData = this.getApplicant();
			return !!(currentApplicantData?.firstName && currentApplicantData?.lastName);
		},
		maxCoApplicants() {
			return this.type === "lease" ? 1 : 3;
		},
		...mapState({
			authUser: state => state.auth.authUser,
			authUserVendor: state => state.auth.authUserVendor,
			locale: state => state.app.locale,
			optionSets: state => state.app.options//TODO: remove, seems to be unused. Unknown for sure at the moment
		}),
		currentApplicantModel() {
			return (this.selectedAppIndex === 'main' ? this.applicant : this.coApps[this.selectedAppIndex]) ?? {};
		},
		currentApplicantErrors() {
			return this.selectedAppIndex === 'main' ? this.errors : this.addApplicantErrors;
		},
		formsJson() {
			return ApplicationConfig;
		}
	},
	methods: {
		...mapActions([
			'saveLoading',
			'showErrorAlert',
			'showSuccessAlert'
		]),
		async updateLeaseTaxesInBackground(newProvince) {
            const appInfo = this.$store.state.application.info;
            const currentProgram = appInfo?.data?.program || appInfo?.program || this.vModels.program;

            if (!currentProgram || !currentProgram.payment) return;

            const PROVINCIAL_TAX_RATES = {
                "AB": 0.05, "BC": 0.12, "MB": 0.12, "NB": 0.15, "NL": 0.15,
                "NT": 0.05, "NS": 0.14, "NU": 0.05, "ON": 0.13, "PE": 0.15,
                "QC": 0.14975, "SK": 0.11, "YT": 0.05
            };
            console.log("refresh Application Datat!!!!!");
            const newTaxRate = PROVINCIAL_TAX_RATES[newProvince] || 0.13;

            const paymentForMath = Number(currentProgram.payment);
            const totalPayments = Number(currentProgram.totalPayments);

            const salesTax = newTaxRate * paymentForMath;
            const paymentWithTax = paymentForMath + salesTax;
            const totalPaymentsWithTax = totalPayments * (1 + newTaxRate);

            const updatedProgram = {
                ...currentProgram,
                taxRate: Number(newTaxRate.toFixed(4)),
                salesTax: salesTax,
                paymentWithTax: paymentWithTax,
                totalPaymentsWithTax: totalPaymentsWithTax,
                initialPaymentWithTax: paymentWithTax 
            };

            this.vModels.program = updatedProgram;
            try {
                await this.$store.dispatch('saveApplicationAction', {
                    data: {
                        program: updatedProgram,
                        id: this.app.application.id
                    }
                });
            } catch (e) {
                console.error("Failed to update taxes in background", e);
            }
        },
		async addApplicant() {
			const firstNameInput = document.getElementById('firstName');
			if (firstNameInput) {
				firstNameInput.focus();
			}
			this.saveLoading(true);
			const failed = this.validateValues();
			if (!failed) {
				await this.saveApplicant();
				this.$nextTick(() => {
					this.loadCanadaPostScripts();
				});
			}
			this.saveLoading(false);
		},
		applicantHasEmail() {
			let result = 'Enter an email to send a link';
			const email = this.currentApplicantModel.email ?? '';
			if (email !== '') {
				result = this.isInvalidEmail(email) || ''
			}
			return result;
		},
		checkValues(applicant, original, section) {
			let allRequiredFound = true;
			let hasChanged = false;
			const optional = this.getOptional(section);
			const required = this.getRequired(section);
			for (const field of required) {
				if ((applicant?.[field] ?? "") === "") {
					allRequiredFound = false;
				}
				if ((original?.[field]?.toUpperCase() ?? "") !== (applicant?.[field]?.toUpperCase() ?? "")) {
					hasChanged = true;
				}
			}
			if (allRequiredFound && !hasChanged) {
				for (const field of optional) {
					if ((original?.[field]?.toUpperCase() ?? "") !== (applicant?.[field]?.toUpperCase() ?? "")) {
						hasChanged = true;
						break;
					}
				}
			}
			return { changed: hasChanged, requiredFound: allRequiredFound };
		},
		getOptional(name) {
			let result = [];
			switch (name) {
				case "details":
					result = [
						"address2",
						"mobilePhone",
						"sin"
					];
					break;
				default:
					result = [
						"email",
						"middleInitial"
					];
					break;
			}
			return result;
		},
		getRequired(name) {
			let result = [];
			switch (name) {
				case "details":
					result = [
						"address1",
						"city",
						"dob",
						"employer",
						"employmentTerm",
						"employmentTitle",
						"grossIncome",
						"postal",
						"primaryPhone",
						"province",
						"titleOwner"
					];
					break;
				default:
					result = [
						"firstName",
						"lastName"
					];
					break;
			}
			return result;
		},
		hasAlreadyRequired(section) {
			const isApplicant = (this.selectedAppIndex ?? "main") === 'main';
			const applicant = isApplicant ? this.getApplicant() : this.app?.application?.coApps?.[this.selectedAppIndex];
			let result = true;
			const required = this.getRequired(section);
			for (const field of required) {
				if ((applicant?.[field] ?? "") === "") {
					result = false;
					break;
				}
			}
			return result;
		},
		hasApplicantChanged(section) {
			const isApplicant = (this.selectedAppIndex ?? "main") === 'main';
			const originalData = isApplicant ? this.getApplicant() : this.app?.application?.coApps?.[this.selectedAppIndex];
			return this.checkValues(this.currentApplicantModel, originalData ?? {}, section);
		},
		async saveDetails() {
			const failed = this.validateValues();
			if (!failed) {
				this.saveLoading(true);
				await this.saveApplicant();
				this.saveLoading(false);
			}
		},
		isInvalidEmail(email) {
			let result = false;
			const isApplicant = (this.selectedAppIndex ?? "main") === "main";
			if (/(.+)@(.+){2,}\.(.+){2,}/.test(email)) {
				if (this.emailMatchesDealerAccount(email)) {
					result = 'This email cannot be the same as the dealer account email';
				} else {
					const compiledEmails = [];
					const coAppsEmails = [];
					for (const applicantIndex in this.app.application.coApps ?? []) {
						const index = Number(applicantIndex);
						if (this.app.application?.coApps?.[index]?.email && index !== this.selectedAppIndex) {
							coAppsEmails.push(this.app.application.coApps?.[index]?.email?.toUpperCase());
						}
					}
					if (!isApplicant) {
						compiledEmails.push(this.getApplicant()?.email?.toUpperCase());
					}
					for (const coAppEmail of coAppsEmails) {
						compiledEmails.push(coAppEmail);
					}
					if (compiledEmails.includes(email?.toUpperCase())) {
						let text = "the main applicant";
						if (coAppsEmails.includes(email?.toUpperCase())) {
							text =  "a co-applicant";
						}
						result = `This email is already used by ${text}`;
					}
				}
			} else {
				result = `Please check email`;
			}
			return result;
		},
		validateValues() {
			let failed = false;
			let errorMessage;
			const fieldsToValidate = [
				"dob",
				"email",
				"employmentTerm",
				"grossIncome"
			];
			const applicantData = this.currentApplicantModel;
			for (const field of fieldsToValidate) {
				if ((applicantData?.[field] ?? '') !== "") {
					if (field === "email") {
						const emailError = this.isInvalidEmail(applicantData.email);
						if (emailError) {
							errorMessage = emailError;
							break;
						}
					} else if (field === "dob") {
						if (!this.isDobValid(applicantData.dob)) {
							errorMessage = 'Use format mm/dd/yyyy for Date of Birth. Applicants must be between 18 and 100 years of age.';
							break;
						}
					} else if (field === "employmentTerm") {
						if (!this.isEmploymentTermValid(applicantData.employmentTerm)) {
							errorMessage = 'Employment should be less than 70 years. Use format mm/dd/yyyy for Employment Since.';
							break;
						}
					} else if (field === "grossIncome") {
						if (applicantData.grossIncome < 10000) {
							errorMessage = 'Gross Income should be more than $10,000.';
							break;
						}
					}
				}
			}
			if (errorMessage) {
				this.errorAlert = this.showErrorAlert(errorMessage);
				failed = true;
			}
			return failed;
		},
		upperCaseFields() {
			["email", "firstName", "lastName", "middleInitial"].forEach(element => {
				const raw = this.currentApplicantModel?.[element] ?? '';
				if (this.currentApplicantModel?.[element]) {
					this.currentApplicantModel[element] = raw !== '' ? String(raw).toUpperCase() : '';
				}
			});
		},
		getApplicant() {
			return this.app?.application;
		},
		makeApplicantObject(data) {
			return {
				address1: data?.address1,
				address2: data?.address2,
				city: data?.city,
				country: data?.country,
				dob: data?.dob,
				email: data?.email ,
				employer: data?.employer,
				employmentTerm: data?.employmentTerm,
				employmentTitle: data?.employmentTitle,
				firstName: data?.firstName ?? '',
				grossIncome: data?.grossIncome,
				lastName: data?.lastName ?? '',
				linkPurpose: data?.linkPurpose,
				linkSentOn: data?.linkSentOn,
				middleInitial: data?.middleInitial,
				postal: data?.postal,
				primaryPhone: data?.primaryPhone,
				province: data?.province,
				titleOwner: this.normalizeTitleOwner(data?.titleOwner, null)
			}
		},
		getTitleOwnerEmptyOption() {
			const titleOwnerOptions = this.getForm('2.2')?.titleOwner?.options;
			if (!titleOwnerOptions) {
				return ' ';
			}
			if (Array.isArray(titleOwnerOptions)) {
				const matching = titleOwnerOptions.find(option => {
					const optionValue = option?.value;
					return typeof optionValue === 'string' && !optionValue.trim();
				});
				if (matching && typeof matching.value === 'string') {
					return matching.value;
				}
				return titleOwnerOptions.includes('-') ? '-' : ' ';
			}
			const optionKeys = Object.keys(titleOwnerOptions);
			const emptyOptionKey = optionKeys.find(optionKey => !String(optionKey).trim());
			if (emptyOptionKey !== undefined) {
				return emptyOptionKey;
			}
			if (optionKeys.includes('-')) {
				return '-';
			}
			return ' ';
		},
		normalizeTitleOwner(value, fallback = null) {
			const emptyOption = this.getTitleOwnerEmptyOption();
			const allowedValues = [emptyOption, '-', 'Own', 'Rent'];
			const safeFallback = fallback === null ? emptyOption : fallback;
			const normalize = candidate => {
				if (typeof candidate !== 'string') {
					return null;
				}
				const trimmed = candidate.trim();
				if (!trimmed) {
					return emptyOption;
				}
				if (trimmed === '-' && emptyOption !== '-') {
					return emptyOption;
				}
				if (trimmed === 'Own' || trimmed === 'Rent') {
					return trimmed;
				}
				return allowedValues.includes(trimmed) ? trimmed : null;
			};
			const normalizedValue = normalize(value);
			if (normalizedValue !== null) {
				return normalizedValue;
			}
			return normalize(safeFallback);
		},
		onFieldInput(fieldName, value) {
			if (fieldName === "titleOwner") {
				value = this.normalizeTitleOwner(value, this.currentApplicantModel.titleOwner);
			}
			this.currentApplicantModel[fieldName] = value;
		},
		normalizeCoAppTitleOwners(coApps = []) {
			const newCoApps = [];
			for (const coApplicant of coApps) {
				newCoApps.push(this.makeApplicantObject(coApplicant));
			}
			return newCoApps;
		},
		updateIsMaxApplicants() {
			const coAppCount = Array.isArray(this.coApps) ? this.coApps.length : 0;
			this.isMaxApplicants = coAppCount >= this.maxCoApplicants;
		},
		emailMatchesDealerAccount(email) {
			const d = this.authUser && this.authUser.email;
			if (!email || !d) {
				return false;
			}
			return String(email).toLowerCase().trim() === String(d).toLowerCase().trim();
		},
		handleAddApplicantEnter() {
			// Only trigger save-on-enter while adding a new applicant
			if (this.hasApplicantChanged("names")?.changed ?? false) {
				this.addApplicant();
			}
		},
		async saveApplicant() {
			this.upperCaseFields();
			await saveApplication({
				id: this.app.application.id,
				...this.applicant,
				coApps: this.coApps,
			});
			this.app = await this.getApplication();
			this.applicant = this.makeApplicantObject(this.getApplicant());
			this.coApps = this.normalizeCoAppTitleOwners(this.app?.application?.coApps);
			this.updateIsMaxApplicants();
			this.setNextDisabled(!this.hasApplicant);
		},
		async removeApplicant(index) {
			const canSave = this.checkCanSave();
			if (canSave || this.selectedAppIndex === index) {
				this.saveLoading(true);
				this.coApps.splice(index, 1);
				await this.saveApplicant();
				if (this.selectedAppIndex !== "main") {
					let newIndex = this.selectedAppIndex;
					if (this.selectedAppIndex >= this.coApps.length) {
						newIndex -= 1;
					}
					if (newIndex < 0) {
						newIndex = "main";
					}
					await this.selectApp(newIndex, false);
				}
				this.saveLoading(false);
			} else {
				await this.showErrorAlert("Fill in required fields");
			}
		},
		async saveAndMove() {
			if (this.nextStep || this.previousStep || this.exiting || this.saveStep.saving) {
				const allowSkipSave = this.nextStep || this.exiting || this.previousStep && (this.hasApplicant && !this.hasApplicantChanged("names").changed);
				let failed = false;
				if (!this.alreadySaved) {
					failed = await this.saveApp(allowSkipSave);
				}
				this.alreadySaved = !failed;
				if (!failed) {
					if (this.saveStep.saving) {
						if (this.saveStep.onSuccess) {
							this.saveStep.onSuccess();
						}
					} else if (this.nextStep) {
						this.goToNextStep();
					} else if (this.previousStep) {
						this.goToPreviousStep();
					} else {
						this.exitApplication();
					}
				} else {
					if (this.saveStep.onError) {
						this.saveStep.onError();
					}
				}
			}
		},
		checkSection(section) {
			let result = false;
			const checks = this.hasApplicantChanged(section);
			if (checks.changed) {
				if (checks.requiredFound) {
					result = true;
				}
			}
			return result;
		},
		checkCanSave() {
			let result = true;
			const nameChecks = this.hasApplicantChanged("names");
			if (nameChecks.changed) {
				if (!nameChecks.requiredFound) {
					result = false;
				}
			}
			return result;
		},
		async selectApp(index, isSave = true) {
			const canSave = this.checkCanSave();
			const failed = this.validateValues();
			if (!failed && index !== (this.selectedAppIndex ?? "main")) {
				if (canSave) {
					this.saveLoading(true);
					if (isSave) {
						await this.saveApplicant();
					}
					this.selectedAppIndex = index ?? "main";
					this.saveLoading(false);
				} else {
					await this.showErrorAlert("Fill in required fields");
				}
			}
			if (this.hasAlreadyRequired("names")) {
				this.$nextTick(() => {
					this.loadCanadaPostScripts();
				});
			}
		},
		async sendLink() {
			this.saveLoading(true);
			const result = await callAction({
				action: 'sendLink',
				id: this.app.application.id,
				for: this.selectedAppIndex ?? "main",
				domain: window.location.origin + '/',
				dealer: this.authUser.vendorName ?? this.authUser.name,
				purpose: 'application'
			})
			if (result.data.application) {
				await this.showSuccessAlert('Successfully sent link.');
				this.app = await this.getApplication();
				this.applicant = this.makeApplicantObject(this.getApplicant());
				this.coApps = this.normalizeCoAppTitleOwners(this.app?.application?.coApps);
			} else {
				await this.showErrorAlert(result.data);
			}
			this.saveLoading(false);
		},
		async cancelLink() {
			this.saveLoading(true);
			await callAction({
				action: 'cancelLink',
				id: this.app.application.id,
				for: this.selectedAppIndex,
				domain: window.location.origin + '/'
			})
			this.app = await this.getApplication();
			this.applicant = this.makeApplicantObject(this.getApplicant());
			this.coApps = this.normalizeCoAppTitleOwners(this.app?.application?.coApps);
			this.saveLoading(false);
		},
		async prepareNewApplicant() {
			this.saveLoading(true);
			const canSave = this.checkCanSave();
			if (canSave) {
				const newIndex = this.coApps.length;
				if (this.hasApplicant) {
					this.coApps[newIndex] = this.makeApplicantObject({
						address1: this.addApplicantVModels.address1 || this.vModels.address1,
						address2: this.addApplicantVModels.address2 || this.vModels.address2,
						city: this.addApplicantVModels.city || this.vModels.city,
						email: this.addApplicantVModels.email || this.vModels.email,
						firstName: this.addApplicantVModels.firstName || this.vModels.firstName,
						lastName: this.addApplicantVModels.lastName || this.vModels.lastName,
						middleInitial: this.addApplicantVModels.middleInitial || this.vModels.lastName,
						postal: this.addApplicantVModels.postal || this.vModels.postal,
						province: this.addApplicantVModels.province || this.vModels.province,
						titleOwner: this.normalizeTitleOwner(this.addApplicantVModels.titleOwner, this.vModels.titleOwner)
					});
				}
				await this.saveApplicant();
				this.selectedAppIndex = newIndex;
				this.$nextTick(() => {
					const firstNameInput = document.getElementById('firstName');
					if (firstNameInput) {
						firstNameInput.focus();
					}
				});
			} else {
				await this.showErrorAlert("Fill in required fields");
			}
			this.saveLoading(false);
		},
		isDateValid(dateString) {
			let result = false;
			const pattern = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
			if (pattern.test(dateString)) {
				const [month, day, year] = dateString.split("/").map(Number);
				const date = new Date(year, month - 1, day);
				if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
					result = date;
				}
			}
			return result;
		},
		isDobValid(dateString) {
			let result = false;
			const date = this.isDateValid(dateString);
			if (date) {
				const today = new Date();
				const eighteenYearsAgo = new Date(
					today.getFullYear() - 18,
					today.getMonth(),
					today.getDate()
				);
				const hundredYearsAgo = new Date(
					today.getFullYear() - 100,
					today.getMonth(),
					today.getDate()
				);
				result = date <= eighteenYearsAgo && date >= hundredYearsAgo;
			}
			return result;
		},
		isEmploymentTermValid(dateString) {
			let result = false;
			const date = this.isDateValid(dateString);
			if (date) {
				const today = new Date();
				const seventyYearsAgo = new Date(
					today.getFullYear() - 70,
					today.getMonth(),
					today.getDate()
				);
				result = date <= today && date >= seventyYearsAgo;
			}
			return result;
		},
		async saveApp(savingForLater = false) {
			this.saveLoading(true);
			const canSave = this.checkCanSave();
			let failed = this.validateValues();
			const needsSave = this.hasApplicantChanged("names")?.changed || this.hasApplicantChanged("details")?.changed;
			if (!failed) {
				if (canSave || savingForLater) {
					Object.keys(this.currentApplicantModel).forEach(element => {
						let nextValue = this.currentApplicantModel[element];
						if (element === 'titleOwner') {
							nextValue = this.normalizeTitleOwner(this.currentApplicantModel[element], null);
							this.currentApplicantModel.titleOwner = nextValue;
						}
						this.currentApplicantModel[element] = nextValue
					});
					if (needsSave) {
						await this.saveApplicant();
					}
				} else {
					failed = true;
					await this.showErrorAlert("Fill in required fields");
				}
			}
			this.saveLoading(false);
			return failed;
		},
		loadCanadaPostScripts() {
			const apiKey = process.env.VUE_APP_CANADA_POST_API_KEY;
			const applyCanadaPostAddress = (address, sourceTag) => {
				const model = this.currentApplicantModel;
				model.address1 = address.Line1;
				model.address2 = address.Line2 || '';
				model.city = address.City;
				model.postal = address.PostalCode;
				const provinceName = address.ProvinceName;
				if (this.addressForm && this.addressForm.province) {
					const provinceOptions = this.addressForm.province.options || {};
					const provinceEntry = Object.entries(provinceOptions).find(([_key, value]) => value.en === provinceName);
					model.province = provinceEntry && provinceEntry[0] ? provinceEntry[0] : '';
				} else {
					model.province = '';
				}
			};
			// Destroy existing control if it exists
			if (this.canadaPostControl) {
				this.canadaPostControl.destroy();
				this.canadaPostControl = null;
			}
			// Only load scripts once
			if (!this.canadaPostScriptsLoaded) {
				let addressCompleteScript = document.createElement('script');
				let addressCompleteCSS = document.createElement('link');
				addressCompleteCSS.setAttribute('rel', 'stylesheet');
				addressCompleteCSS.setAttribute('type', 'text/css');
				addressCompleteCSS.setAttribute('href', `https://ws1.postescanada-canadapost.ca/css/addresscomplete-2.30.min.css?key=${apiKey}`);
				document.head.appendChild(addressCompleteCSS);
				addressCompleteScript.setAttribute('src', `https://ws1.postescanada-canadapost.ca/js/addresscomplete-2.30.min.js?key=${apiKey}&culture=en`);
				addressCompleteScript.onload = () => {
					let fields = [
						{ element: "address1", field: "Line1", mode: pca.fieldMode.DEFAULT },
						{ element: "address2", field: "Line2", mode: pca.fieldMode.POPULATE },
						{ element: "city", field: "City", mode: pca.fieldMode.POPULATE },
						{ element: "postal", field: "PostalCode", mode: pca.fieldMode.POPULATE },
					];
					let options = {
						key: apiKey
					};
					this.canadaPostControl = new pca.Address(fields, options);
					this.canadaPostControl.listen('populate', (address) => {
						applyCanadaPostAddress(address, 'onload');
					});
				};
				document.head.appendChild(addressCompleteScript);
				this.canadaPostScriptsLoaded = true;
			} else {
				// Scripts already loaded, create control immediately
				let fields = [
					{ element: "address1", field: "Line1", mode: pca.fieldMode.DEFAULT },
					{ element: "address2", field: "Line2", mode: pca.fieldMode.POPULATE },
					{ element: "city", field: "City", mode: pca.fieldMode.POPULATE },
					{ element: "postal", field: "PostalCode", mode: pca.fieldMode.POPULATE },
				];
				let options = {
					key: apiKey
				};
				this.canadaPostControl = new pca.Address(fields, options);
				this.canadaPostControl.listen('populate', (address) => {
					applyCanadaPostAddress(address, 'reload');
				});
			}
		},
		getForm(key) {
			if (!this.formsJson) {
				return;
			}
			return this.formsJson[key];
		},
		getLabel(fieldName) {
			if (/^row/.test(fieldName)) {
				return '';
			}
			if (this.countryValue === 'USA') {
				switch (fieldName) {
					case 'province':
						return this.$i18n.t('newApplication.state');
					case 'postal':
						return this.$i18n.t('newApplication.zipCode');
					case 'prevProvince':
						return this.$i18n.t('newApplication.prevState');
					case 'prevPostal':
						return this.$i18n.t('newApplication.prevZipCode');
				}
			}
			return this.$i18n.t('newApplication.' + fieldName);
		},
		checkApplicant(applicant) {
			let result = true;
			if (!applicant) {
				result = false;
			}
			let required = [
				'address1',
				'city',
				'dob',
				'employer',
				'employmentTerm',
				'employmentTitle',
				'firstName',
				'grossIncome',
				'lastName',
				'postal',
				'primaryPhone',
				'province',
				'titleOwner',
			]
			for (const field of required) {
				if ((applicant?.[field]?.trim?.() || '-') === '-') {
					result = false;
					break;
				}
			}
			return result;
		},
		checkForOptions(name, data) {
			let val;
			if (['province', 'prevProvince'].includes(name)) {
				val = this.optionSets?.provinces
			} else if (name === 'coborrowerRelationship') {
				val = this.optionSets['relationships'];
			} else if (Object.keys(this.optionSets ?? {}).includes(name)) {
				val = this.optionSets[name];
			}
			if (name === 'province') {
				let onlyProv = this.authUserVendor && this.authUserVendor.province;
				if (val && onlyProv && val[onlyProv]) {
					this.enableOnly[name] = onlyProv;
				}
			} else if (name === 'country') {
				const isUSA = (['US', 'USA'].includes(this.authUserVendor && this.authUserVendor.country));
				this.enableOnly[name] = isUSA ? 'USA' : 'CA';
			}
			if (isObject(val) || isArray(val)) {
				this.$set(this.options, name, val);
				if (isObject(data)) {
					data.options = this.options[name];
				}
				if (this.currentApplicantModel?.[name] !== undefined) {
					let vModelVal = this.currentApplicantModel?.[name];
					let exist;
					if (isArray(val)) {
						exist = val.filter(item => item === vModelVal);
					} else {
						exist = Object.keys(val).filter(item => item === vModelVal);
					}
					if (!exist.length) {
						this.currentApplicantModel[name] = this.getFirstOption(val);
						this.manuallySetSelectValue(name, this.currentApplicantModel[name]);
					}
				}
			}
			this.$nextTick(function () {
				if (this.currentApplicantModel?.[name] !== undefined) {
					this.manuallySetSelectValue(name, this.currentApplicantModel[name]);
				}
			});
			return data;
		},
		getFirstOption(options) {
			let first;
			Object.keys(options).forEach(optionKey => {
				if (first) {
					return;
				}
				first = optionKey;
			});
			return first;
		},
		manuallySetSelectValue(fieldName, val) {
			// Hack to make the fields use a default value
			let elm = document.querySelector(`select[name=${fieldName}]`);
			if (elm) {
				elm.childNodes.forEach(child => {
					if (child.value === val) {
						child.selected = true;
					}
				});
				if (val !== elm.value) {
					elm.value = val;
					elm.dispatchEvent(new CustomEvent('change', elm));
				}
			}
		},
		onChange: throttle(100, function (val) {
			// for MS Edge
			if (navigator.userAgent.indexOf('Edge') !== -1) {
				if (window.event?.type === 'change' && this.currentApplicantModel?.[window?.event?.target?.name] !== val) {
					this.currentApplicantModel[window.event.target.name] = val;
				}
			}
			this.$nextTick(() => {
				if (!this.validated && !this.validateAll) {
					return;
				}
				this.errors = this.validateRequiredFields(this.currentApplicantModel);
			});
		}),
		validateRequiredFields(fields, scroll = false) {
			let response = false;
			let schema = Object.assign({}, this.joiSchema);
			let errors = ApplicationValidator.validate(fields, schema, this.locale);
			if (errors) {
				Object.keys(errors).forEach(i => {
					if (!isObject(response)) {
						response = {};
					}
					response[errors[i].key] = errors[i].message;
				});
			}
			this.validated = true;
			if (scroll) {
				setTimeout(function () {
					let invalids = document.querySelectorAll(".was-validated, .md-invalid");
					if (invalids && invalids[0]) {
						this.$scrollTo(invalids[0]);
					}
				}.bind(this), 150);
			}
			return response;
		},
	}
};
</script>

<template>
	<div class="add-applicant-template">
		<div class="container-xl">
			<div class="row">
				<div class="col-24">
					<div class="template__page-title">
						<h1>APPLICANT DETAILS</h1>
					</div>
				</div>
			</div>
		</div>
		<form novalidate>
			<div class="container-xl template--vh">
				<div class="row">
					<div class="col-lg-8">
						<Program :programType="programType"
						         :financedAmount="vModels.program.isStepLoan ? vModels.program.amount : vModels.program.financedAmount || vModels.program.totalPayments"
						         :periodPayment="vModels.program.isStepLoan ? vModels.program.periodPaymentPhaseOne : vModels.program.periodPayment"
						         :initialPayment="vModels.program.initialPayment ?? null"
						         :initialPaymentWithTax="vModels.program.initialPaymentWithTax ?? null"
						         :equipmentPrice="vModels.program.equipmentPrice ?? null"
						         :leaseTermMonths="vModels.program.leaseTermMonths ?? null"
						         :serviceTotal="vModels.program.serviceTotal ?? null"
						         :totalPaymentsWithTax="vModels.program.totalPaymentsWithTax ?? null"
						         :apr="vModels.program.apr ?? null" />
						<div class="template--section-divider"></div>
						<!-- Add Applicant Form Section -->
						<div class="tffi-card tffi-card-main-card" v-if="coApps?.length === maxCoApplicants">
							<div class="row">
								<div class="col-24">
									<p>Maximum number of co-applicants reached ({{ maxCoApplicants }})</p>
								</div>
							</div>
						</div>
						<!-- Applicant Selection Boxes - In first column -->
						<div @click="selectApp('main')"
						     :class="'tffi-card tffi-card-secondary-card theming primary-color ' + (selectedAppIndex === 'main' ? 'active' : '')"
						     style="cursor: pointer">
							<div class="d-none- tffi-card-secondary-card__left-area">
								<div class="tffi-card-secondary-card__left-area__status-indicator">
									<div :class="`status-${checkApplicant(applicant) ? 'accepted' : 'denied'}`">
										<img :src="checkApplicant(applicant) ? checkMark : exclamationMark" alt="Status">
									</div>
								</div>
							</div>
							<div class="tffi-card-secondary-card__right-area">
								<div class="tffi-card-secondary-card__right-area__title-area">
									<h2>Applicant</h2>
								</div>
								<div class="tffi-card-secondary-card__right-area__content-area">
									<p>{{ applicant.firstName + ' ' + (applicant.middleInitial ?? '') + ' ' + applicant.lastName }}</p>
									<p>{{ applicant.email }}</p>
								</div>
								<div v-if="applicant?.linkSentOn && applicant?.linkPurpose === 'application'"
								     class="tffi-card-secondary-card__right-area__link-confirmation-area">
									<ul class="">
										<li>Link Sent</li>
									</ul>
								</div>
							</div>
						</div>
						<div v-if="coApps?.length > 0" v-for="(coApp, index) in coApps" @click="selectApp(index)"
						     :class="'tffi-card tffi-card-secondary-card ' + (selectedAppIndex === index ? 'active' : '')"
						     style="cursor: pointer">
							<div class="d-none- tffi-card-secondary-card__left-area">
								<div class="tffi-card-secondary-card__left-area__status-indicator">
									<div :class="`status-${checkApplicant(coApps[index]) ? 'accepted' : 'denied'}`">
										<img :src="checkApplicant(coApps[index]) ? checkMark : exclamationMark" alt="Status">
									</div>
								</div>
							</div>
							<div class="tffi-card-secondary-card__right-area">
								<div class="tffi-card-secondary-card__right-area__title-area">
									<h2>Co-Applicant</h2>
								</div>
								<div class="tffi-card-secondary-card__right-area__content-area">
									<p>{{ coApp.firstName + ' ' + (coApp.middleInitial ?? '') + ' ' + coApp.lastName }}</p>
									<p>{{ coApp['email'] }}</p>
								</div>
								<div v-if="coApp?.linkSentOn && coApp?.linkPurpose === 'application'"
								     class="tffi-card-secondary-card__right-area__link-confirmation-area">
									<ul class="">
										<li>Link Sent</li>
									</ul>
								</div>
								<button type="button" @click.stop="removeApplicant(index)"
								        class="tffi-card-secondary-card__right-area__trash-area">
									<img :src="garbageCan" alt="">
								</button>
							</div>
						</div>
						<!-- Add Applicant button under cards (left column) -->
						<div v-if="hasApplicant && !isMaxApplicants" class="mt-3 mb-4">
							<button
								class="tffi-btn tffi-btn--solid theming accent-color accent-color-btn-solid w-100 fw-bold"
								type="button"
								@click="prepareNewApplicant">
								Add Applicant
							</button>
						</div>
					</div>
					<div class="col-lg-16">
						<div class="tffi-card tffi-card-main-card" v-if="!isMaxApplicants || selectedAppIndex !== null" @keyup.enter.prevent="handleAddApplicantEnter">
							<div class="row">
								<div class="col-24">
									<h2>{{ (selectedAppIndex ?? "main") === 'main' ? 'Applicant' : 'Co-Applicant' }}</h2>
								</div>
							</div>
							<div class="row row-cols-lg-3">
								<template v-for="(fieldData, fieldName) in getForm('1')">
									<div :class="fieldData.class">
										<div class="d-flex flex-column">
											<form-field v-bind="checkForOptions(fieldName, fieldData)" v-model="currentApplicantModel[fieldName]" :name="fieldName"
											            :label="getLabel(fieldName)" :id="fieldName"
											            :options="(options[fieldName]) ? options[fieldName] : fieldData.options"
											            :error="currentApplicantErrors[fieldName]" :locale="locale" autocomplete="off" bootstrap
											            @change="onChange" @input="onFieldInput(fieldName, $event)">
												<div class="invalid-feedback"></div>
											</form-field>
										</div>
									</div>
								</template>
							</div>
							<div class="row">
								<div class="col-24 col-lg-16 my-3">
									<div class="d-flex align-items-center h-100">
										<p class="theming primary-color my-0 d-none" v-if="applicant.firstName">
											* If there are no more co-applicants, click NEXT.
										</p>
									</div>
								</div>
								<div class="col-24 col-lg-8 my-3">
									<div class="d-flex justify-content-end">
										<button
											class="tffi-btn tffi-btn--solid theming accent-color accent-color-btn-solid mx-auto-lg fw-bold w-75"
											:disabled="!checkSection('names')"
											type="button" @click="addApplicant">
											{{ hasAlreadyRequired("names") ? "Update": "Save" }}
										</button>
									</div>
								</div>
							</div>
						</div>
						<div v-if="hasAlreadyRequired('names')" class="tffi-card tffi-card-main-card">
							<div class="row">
								<div class="col-24 col-sm-14 col-md-14 col-lg-14 col-xl-14 d-flex align-items-center mb-4 mb-sm-0">
									<p class="my-0">Select an applicant and click "Send Link" to email a credit application for completion
										or complete below</p>
								</div>
								<div
									class="col-24 col-sm-5 col-md-5 col-lg-5 col-xl-5 d-flex align-items-center justify-content-center justify-content-xl-end mb-3 mb-sm-0">
									<button type="button"
									        class="tffi-btn tffi-btn--solid theming accent-color accent-color-btn-solid mx-auto-lg w-100 text-nowrap"
									        v-if="currentApplicantModel?.linkSentOn && currentApplicantModel?.linkPurpose === 'application'" @click="cancelLink">
										Cancel Link
									</button>
								</div>
								<div
									class="col-24 col-sm-5 col-md-5 col-lg-5 col-xl-5 d-flex align-items-center justify-content-center justify-content-xl-end mb-0 mb-sm-0">
									<button class="tffi-btn tffi-btn--solid theming primary-color primary-color-btn-solid mx-auto-lg w-100 text-nowrap" @click="sendLink" :disabled="applicantHasEmail() !== ''" :title="applicantHasEmail()" type="button">
										{{ (currentApplicantModel?.linkSentOn && currentApplicantModel?.linkPurpose === 'application') ? 'Resend Link' : 'Send Link'}}
									</button>
								</div>
							</div>
						</div>
						<div v-if="hasAlreadyRequired('names') && !(currentApplicantModel.linkSentOn && currentApplicantModel.linkPurpose === 'application')">
							<div class="container-fluid">
								<div class="row">
									<div class="col-24 px-0">
										<div class="template__secondary-title">
											<h2 class="mb-0">Applicant Details</h2>
										</div>
									</div>
								</div>
							</div>
							<div class="tffi-card tffi-card-main-card">
								<div class="row row-cols-md-3">
									<template v-for="(fieldData, fieldName) in getForm('2')">
										<div v-if="fieldName !== 'firstName' && fieldName !== 'lastName' && fieldName !== 'middleInitial'"
										     :class="fieldData.class">
											<div class="d-flex flex-column">
												<form-field v-bind="checkForOptions(fieldName, fieldData)" v-model="currentApplicantModel[fieldName]"
												            :name="fieldName" :label="getLabel(fieldName)"
												            :options="(options[fieldName]) ? options[fieldName] : fieldData.options"
												            :error="errors[fieldName]" :locale="locale" bootstrap @change="onChange"
												            @input="onFieldInput(fieldName, $event)"></form-field>
											</div>
										</div>
									</template>
								</div>
								<div class="row row-cols-md-2">
									<template v-for="(fieldData, fieldName) in getForm('2.0')">
										<div :class="fieldData.class">
											<div class="d-flex flex-column">
												<form-field v-bind="checkForOptions(fieldName, fieldData)" v-model="currentApplicantModel[fieldName]"
												            :name="fieldName" :label="getLabel(fieldName)"
												            :options="(options[fieldName]) ? options[fieldName] : fieldData.options"
												            :error="errors[fieldName]" :locale="locale" bootstrap @change="onChange"
												            @input="onFieldInput(fieldName, $event)"></form-field>
											</div>
										</div>
									</template>
								</div>
								<div class="row row-cols-md-2">
									<template v-for="(fieldData, fieldName) in addressForm">
										<div :class="fieldData.class">
											<div class="d-flex flex-column">
												<form-field v-bind="checkForOptions(fieldName, fieldData)" v-model="currentApplicantModel[fieldName]"
												            :name="fieldName" :label="getLabel(fieldName)"
												            :options="(options[fieldName]) ? options[fieldName] : fieldData.options"
												            :error="currentApplicantErrors[fieldName]" :locale="locale" bootstrap @change="onChange"
												            @input="onFieldInput(fieldName, $event)"></form-field>
											</div>
										</div>
									</template>
								</div>
								<div class="row row-cols-md-2">
									<template v-for="(fieldData, fieldName) in getForm('2.2')">
										<div :class="fieldData.class">
											<div class="d-flex flex-column">
												<form-field v-bind="checkForOptions(fieldName, fieldData)" v-model="currentApplicantModel[fieldName]"
												            :name="fieldName" :label="getLabel(fieldName)"
												            :options="(options[fieldName]) ? options[fieldName] : fieldData.options"
												            :error="errors[fieldName]" :locale="locale" bootstrap @change="onChange"
												            @input="onFieldInput(fieldName, $event)"></form-field>
											</div>
										</div>
									</template>
								</div>
								<div class="d-flex justify-content-end">
									<button
										class="tffi-btn tffi-btn--solid theming accent-color accent-color-btn-solid mx-auto-lg fw-bold w-25"
										:disabled="!checkSection('details')"
										type="button" @click="saveDetails">
										{{ hasAlreadyRequired("details") ? "Update": "Save" }}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</form>
	</div>
</template>

<style lang="scss" scoped>
.float-right {
	float: right;
}
.tffi-card-secondary-card {
	&:hover {
		border: 2px solid #C18F14;
	}
}
</style>
